import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HandCoins, RefreshCw, Landmark, Eye, Printer, FileSpreadsheet, FileText, Clock, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { exportToCSV, printTable } from '../../lib/printUtils';
import { fmtCLP, refrescarDesdeAlegra } from './egresos/egresosUtils';
import { EgresosTable } from './egresos/EgresosTable';
import { EgresoConciliarDialog } from './egresos/EgresoConciliarDialog';
import { EgresoTrazabilidadSheet } from './egresos/EgresoTrazabilidadSheet';
import { IngresoCrearDialog } from './ingresos/IngresoCrearDialog';

const hoyISO = () => new Date().toISOString().slice(0, 10);

function Kpi({ icon: Icon, color, label, value, sub, testId, onClick, active }) {
  return (
    <Card data-testid={testId} onClick={onClick} className={`${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${active ? 'ring-2 ring-emerald-400' : ''}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color.bg}`}><Icon className={`h-5 w-5 ${color.fg}`} /></div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground">{label}</p>
          <p className={`text-2xl font-bold tabular-nums ${color.text || ''}`}>{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function IngresosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [fAplicacion, setFAplicacion] = useState('');
  const [fBanco, setFBanco] = useState('');
  const [fTipo, setFTipo] = useState('');
  const [fQ, setFQ] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refrescandoId, setRefrescandoId] = useState(null);
  const [conciliar, setConciliar] = useState(null);
  const [traza, setTraza] = useState(null);
  const [crear, setCrear] = useState({ open: false, contexto: null });

  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    const movId = searchParams.get('movimiento');
    if (!movId) { setCrear({ open: true, contexto: null }); return; }
    api.get(`/contabilidad/ingresos/contexto-movimiento/${movId}`)
      .then((r) => setCrear({ open: true, contexto: r.data }))
      .catch((err) => { toast.error(err?.response?.data?.detail || 'No se pudo cargar el abono'); setCrear({ open: true, contexto: null }); });
  }, [searchParams]);

  const cerrarCrear = (o) => {
    if (o) return;
    setCrear({ open: false, contexto: null });
    if (searchParams.get('nuevo')) { const p = new URLSearchParams(searchParams); p.delete('nuevo'); p.delete('movimiento'); setSearchParams(p, { replace: true }); }
  };
  const onCreado = (d) => {
    loadAll();
    if (d?.conciliacion && !d.conciliacion.error && crear.contexto?.movimiento) navigate('/finanzas/conciliacion-bancaria');
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fAplicacion) params.set('estado_aplicacion', fAplicacion);
      if (fBanco) params.set('estado_conciliacion', fBanco);
      if (fTipo) params.set('tipo', fTipo);
      if (fQ.trim()) params.set('q', fQ.trim());
      const mov = searchParams.get('movimiento');
      if (mov) params.set('movimiento_id', mov);
      const [k, l] = await Promise.all([api.get('/contabilidad/ingresos/kpis'), api.get(`/contabilidad/ingresos?${params.toString()}`)]);
      setKpis(k.data || null);
      setItems(l.data?.items || []);
      setTotal(l.data?.total ?? 0);
    } catch {
      toast.error('No se pudieron cargar los ingresos');
    } finally { setLoading(false); }
  }, [fAplicacion, fBanco, fTipo, fQ, searchParams]);

  useEffect(() => { const t = setTimeout(loadAll, 200); return () => clearTimeout(t); }, [loadAll]);

  const handleDesconciliar = async (e) => {
    if (!window.confirm(`¿Desconciliar ${e.codigo_conciliacion}? El abono de la cartola vuelve a pendiente. No afecta las facturas cobradas.`)) return;
    try { await api.post(`/contabilidad/ingresos/${e.id}/desconciliar-banco`); toast.success('Ingreso desconciliado'); loadAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const handleRefrescar = async (e) => {
    setRefrescandoId(e.id);
    if (await refrescarDesdeAlegra('ingresos', e)) loadAll();
    setRefrescandoId(null);
  };

  const handleSyncAlegra = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/contabilidad/ingresos/sync-alegra?dias_back=60');
      if (res.data?.already_running) toast.info('Ya hay una sincronización en curso; esperando que termine…');
      else toast.info('Sincronizando cobros con Alegra en segundo plano…');
      const inicio = res.data?.run_started_at || null;
      await new Promise((resolve) => {
        const t = setInterval(async () => {
          try {
            const st = (await api.get('/contabilidad/ingresos/sync-alegra/status')).data || {};
            if (st.running || !st.last_run || (inicio && st.last_run < inicio)) return;
            clearInterval(t);
            if (st.last_error) toast.error(`Sync Alegra falló: ${st.last_error}`);
            else {
              const r = st.last_summary || {};
              toast.success(`Sync completo: ${r.insertados ?? 0} nuevos, ${r.actualizados ?? 0} actualizados (${r.alegra_ins_found ?? 0} cobros en Alegra)`);
            }
            resolve();
          } catch { /* reintentar */ }
        }, 3000);
      });
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error sincronizando con Alegra'); }
    finally { setSyncing(false); }
  };

  const columnas = [
    { key: 'fecha_pago', label: 'Fecha cobro', format: fmtFecha },
    { key: 'fecha_cartola_esperada', label: 'Cartola esp.', format: fmtFecha },
    { key: 'alegra_number', label: 'Recibo de Caja N°', mono: true },
    { key: 'codigo_conciliacion', label: 'Código', mono: true },
    { key: 'rut_cliente', label: 'RUT', mono: true },
    { key: 'razon_social', label: 'Cliente' },
    { key: 'nombre_conciliacion', label: 'Nombre conciliación', mono: true },
    { key: 'facturas_cobradas', label: 'Facturas', format: (v) => (v || []).map((f) => f.numero).join(', ') },
    { key: 'monto', label: 'Monto', align: 'right', mono: true, format: (v) => fmtCLP(v) },
    { key: 'estado_aplicacion', label: 'Aplicación', align: 'center', format: (v) => (v || '').toUpperCase() },
    { key: 'estado_conciliacion_banco', label: 'Banco', align: 'center', format: (v) => (v || '').toUpperCase() },
  ];
  const imprimir = () => {
    if (!items.length) { toast.warning('No hay ingresos para imprimir'); return; }
    printTable({ title: 'Ingresos (cobros de clientes)', subtitle: `${items.length} ingresos · Generado ${new Date().toLocaleString('es-CL')}`, columns: columnas, rows: items });
  };
  const exportar = () => {
    if (!items.length) { toast.warning('No hay ingresos para exportar'); return; }
    exportToCSV({ filename: 'ingresos', title: 'CHIRINO STEEL · Ingresos', columns: [...columnas, { key: 'alegra_payment_id', label: 'ID Alegra' }, { key: 'descripcion', label: 'Descripción' }], rows: items });
  };

  const con = kpis?.conciliacion || {};
  return (
    <div className="space-y-5 page-enter" data-testid="ingresos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins className="h-6 w-6 text-emerald-600" /> Ingresos (Recibos de Caja)
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Pagos recibidos registrados en Alegra (<b>Recibo de Caja N°</b>): qué <b>facturas de venta</b> u otra contracuenta cubre cada uno y si ya
            aparece como <b>abono</b> en la cartola bancaria. Registro y conciliación son procesos independientes.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Button size="sm" onClick={() => setCrear({ open: true, contexto: null })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-nuevo-ingreso"><Plus className="h-3.5 w-3.5 mr-1" /> Registrar Recibo de Caja</Button>
          <Button variant="outline" size="sm" onClick={imprimir} data-testid="btn-print-ingresos"><Printer className="h-3.5 w-3.5 mr-1" /> Imprimir</Button>
          <Button variant="outline" size="sm" onClick={exportar} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-ingresos"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={handleSyncAlegra} disabled={syncing} className="gap-2" data-testid="btn-sync-alegra-ingresos">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sincronizando…' : 'Sync Alegra (60d)'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={FileText} color={{ bg: 'bg-emerald-500/10', fg: 'text-emerald-600', text: 'text-emerald-700' }} label="Cobros de facturas" value={kpis?.cobros_count ?? 0} sub={`${fmtCLP(kpis?.cobros_monto_30d)} últimos 30 días`} testId="kpi-cobros"
          onClick={() => setFTipo(fTipo === 'cobro_factura' ? '' : 'cobro_factura')} active={fTipo === 'cobro_factura'} />
        <Kpi icon={Clock} color={{ bg: 'bg-amber-500/10', fg: 'text-amber-600' }} label="Anticipos de clientes" value={(kpis?.pendientes_count ?? 0) + (kpis?.parciales_count ?? 0)} sub={fmtCLP(kpis?.monto_total_disponible)} testId="kpi-anticipos-clientes"
          onClick={() => setFAplicacion(fAplicacion === 'pendiente,parcial' ? '' : 'pendiente,parcial')} active={fAplicacion === 'pendiente,parcial'} />
        <Kpi icon={Landmark} color={{ bg: 'bg-sky-500/10', fg: 'text-sky-600' }} label="Banco: pendientes" value={con.pendiente?.count ?? 0} sub={`${kpis?.conciliacion_atrasados ?? 0} ya debieran estar en cartola`} testId="kpi-banco-pendientes"
          onClick={() => setFBanco(fBanco === 'pendiente' ? '' : 'pendiente')} active={fBanco === 'pendiente'} />
        <Kpi icon={Eye} color={{ bg: 'bg-orange-500/10', fg: 'text-orange-600', text: 'text-orange-700' }} label="Banco: observados" value={con.observado?.count ?? 0} sub="Revisión manual (P3)" testId="kpi-banco-observados"
          onClick={() => setFBanco(fBanco === 'observado' ? '' : 'observado')} active={fBanco === 'observado'} />
        <Kpi icon={Landmark} color={{ bg: 'bg-emerald-500/10', fg: 'text-emerald-600', text: 'text-emerald-700' }} label="Banco: conciliados" value={con.conciliado?.count ?? 0} sub={fmtCLP(con.conciliado?.monto)} testId="kpi-banco-conciliados"
          onClick={() => setFBanco(fBanco === 'conciliado' ? '' : 'conciliado')} active={fBanco === 'conciliado'} />
        <Kpi icon={Landmark} color={{ bg: 'bg-slate-500/10', fg: 'text-slate-500' }} label="Sin cartola" value={con.no_aplica?.count ?? 0} sub="caja / tarjeta / fondos" testId="kpi-banco-sin-cartola"
          onClick={() => setFBanco(fBanco === 'no_aplica' ? '' : 'no_aplica')} active={fBanco === 'no_aplica'} />
      </div>

      <Card>
        <CardContent className="p-4 flex gap-3 flex-wrap items-center">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Aplicación:</Label>
            <Select value={fAplicacion || 'todos'} onValueChange={(v) => setFAplicacion(v === 'todos' ? '' : v)}>
              <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="filtro-aplicacion"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="aplicado">Facturas cobradas</SelectItem>
                <SelectItem value="pendiente,parcial">Anticipos de clientes</SelectItem>
                <SelectItem value="no_aplica">Otros ingresos</SelectItem>
                <SelectItem value="cancelado">Anulados</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Banco:</Label>
            <Select value={fBanco || 'todos'} onValueChange={(v) => setFBanco(v === 'todos' ? '' : v)}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filtro-banco"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="observado">Observados</SelectItem>
                <SelectItem value="conciliado">Conciliados</SelectItem>
                <SelectItem value="no_aplica">Sin cartola</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Tipo:</Label>
            <Select value={fTipo || 'todos'} onValueChange={(v) => setFTipo(v === 'todos' ? '' : v)}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filtro-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="cobro_factura">Cobros de factura</SelectItem>
                <SelectItem value="anticipo_cliente">Anticipos de clientes</SelectItem>
                <SelectItem value="otro_ingreso">Otros ingresos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Buscar cliente, RUT, factura, código…" value={fQ} onChange={(e) => setFQ(e.target.value)} className="h-8 w-[240px] text-xs" data-testid="filtro-q" />
          <span className="ml-auto text-xs text-muted-foreground" data-testid="ingresos-count">{items.length < total ? `Mostrando ${items.length} de ${total}` : `${total} ingresos`}</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Ingresos {loading ? '(cargando…)' : ''}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center" data-testid="ingresos-empty">
              {fAplicacion || fBanco || fTipo || fQ ? 'Sin resultados con los filtros actuales.' : 'No hay ingresos. Usa "Sync Alegra" para traer los cobros registrados.'}
            </p>
          ) : (
            <EgresosTable items={items} modo="ingreso" hoy={hoyISO()} onConciliar={setConciliar} onDesconciliar={handleDesconciliar} onTrazabilidad={setTraza} onRefrescar={handleRefrescar} refrescandoId={refrescandoId} />
          )}
        </CardContent>
      </Card>

      <EgresoConciliarDialog egreso={conciliar} modo="ingreso" onOpenChange={(o) => !o && setConciliar(null)} onDone={loadAll} />
      <EgresoTrazabilidadSheet egreso={traza} modo="ingreso" onOpenChange={(o) => !o && setTraza(null)} />
      <IngresoCrearDialog open={crear.open} contexto={crear.contexto} onOpenChange={cerrarCrear} onCreated={onCreado} />
    </div>
  );
}
